import { z } from 'zod'

/**
 * Category visual kind. `base` is the default plain look; `accent`
 * paints the row with `color` (and a contrast-picked text colour) on
 * the public site.
 */
export type CategoryKind = 'base' | 'accent'

export const CategorySchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  slug: z.string(),
  /**
   * WP returns 0 for top-level. We don't expose a parent picker in the
   * editor (all categories are currently flat), but we still read it
   * to keep round-trips with the server faithful.
   */
  parentId: z.number().int().nonnegative(),
  /** Number of dishes in this category, surfaced by WP. */
  count: z.number().int().nonnegative(),
  /** `crb_menu_category_type` — affects how the public site renders it. */
  kind: z.union([z.literal('base'), z.literal('accent')]),
  /**
   * `crb_menu_category_color` — `#rrggbb`. Empty string = no colour
   * chosen yet; only meaningful when `kind === 'accent'`.
   */
  color: z.string(),
  /** WP `description` field — plain text, HTML stripped on parse. Optional for back-compat. */
  description: z.string().optional().default(''),
  /**
   * Manual sort key on the term, written by drag-to-reorder. Lives in
   * `wp_terms.term_order` (one column shared with the WP admin "Taxonomy
   * Terms Order" plugin, so admin reorder and SPA reorder stay in sync).
   * Zero for legacy / never-reordered terms — those fall through to id
   * order as the WP secondary sort.
   */
  termOrder: z.number().int().nonnegative(),
})

export type Category = z.infer<typeof CategorySchema>
